import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
  // 60s timeout to accommodate slow mobile networks + large payloads (images, etc.)
  timeout: 60000,
  // Allow up to 25MB payloads (compressed image base64 is usually <500KB but multiple images can stack)
  maxContentLength: 25 * 1024 * 1024,
  maxBodyLength: 25 * 1024 * 1024,
});

// Fallback auth: if Safari/iOS blocks the httpOnly cookie, we also send the JWT
// (stored in localStorage after login) via Authorization header. Backend accepts both.
const TOKEN_KEY = "benova_token";
export function setAuthToken(token) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}
export function getAuthToken() {
  try { return localStorage.getItem(TOKEN_KEY); } catch (e) { return null; }
}
api.interceptors.request.use((config) => {
  const t = getAuthToken();
  if (t) {
    config.headers = config.headers || {};
    if (!config.headers.Authorization) config.headers.Authorization = `Bearer ${t}`;
  }
  return config;
});

export function formatErr(err) {
  // Friendlier messages for common network / payload issues
  if (err?.code === "ECONNABORTED" || /timeout/i.test(err?.message || "")) {
    return "La operación tardó demasiado. Revisa tu conexión y vuelve a intentar (las fotos grandes pueden tardar).";
  }
  if (err?.message === "Network Error") {
    return "No se pudo conectar. Verifica tu internet o intenta con fotos más pequeñas.";
  }
  const status = err?.response?.status;
  if (status === 413) return "Las fotos pesan demasiado. Sube imágenes más ligeras o menos imágenes.";
  if (status === 401) return "Tu sesión expiró. Cierra sesión y vuelve a entrar.";
  const d = err?.response?.data?.detail;
  if (!d) return err?.message || "Error";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return JSON.stringify(d);
}

export function parseVideo(url) {
  if (!url) return null;
  try {
    const u = new URL(url);
    const host = u.hostname.replace("www.", "");
    if (host.includes("youtube.com")) {
      const id = u.searchParams.get("v");
      if (id) return { type: "youtube", src: `https://www.youtube.com/embed/${id}` };
    }
    if (host === "youtu.be") {
      const id = u.pathname.slice(1);
      if (id) return { type: "youtube", src: `https://www.youtube.com/embed/${id}` };
    }
    if (host.includes("vimeo.com")) {
      const parts = u.pathname.split("/").filter(Boolean);
      const id = parts[parts.length - 1];
      if (id) return { type: "vimeo", src: `https://player.vimeo.com/video/${id}` };
    }
  } catch (e) {
    return null;
  }
  return null;
}

// Picks the language version of a field on an experience object.
// If lang is 'en' and the *_en field has content, return it; otherwise fallback to the base (ES) field.
export function pickLang(obj, field, lang) {
  if (!obj) return "";
  if (lang === "en") {
    const enVal = obj[`${field}_en`];
    if (Array.isArray(enVal)) {
      if (enVal.length > 0) return enVal;
    } else if (enVal) {
      return enVal;
    }
  }
  return obj[field] ?? (Array.isArray(obj[`${field}_en`]) ? [] : "");
}
